"use client";

/*
 * @dcyfr-labs/dcyfr-cut-chooser — the visitor's cut chooser for a site
 * whose identity declares cuts (rei-workspace
 * openspec/changes/dcyfr-obsidian-identity, design decision 4, Task 6).
 *
 * A cut is a colour-only sub-axis of one identity, stamped as data-cut on
 * the same <html> element that carries data-identity (theme engine,
 * contract v3). This control lets the visitor pick one. It is props-driven
 * and names no identity itself: the site passes the identity, its base cut,
 * the cut list, one swatch colour per cut and the cookie name.
 *
 * On a pick it does three things, in this order:
 *   1. writes the cookie `<cookieName>=<cut>` (Path=/, Max-Age one year,
 *      SameSite=Lax, Secure when the page is served over https), so the
 *      server stamps the cut on the next request and no page flashes;
 *   2. adds .theme-swap to <html>, restamps data-cut, and removes the class
 *      after two animation frames: the gallery's mechanism, so no transition
 *      plays across the swap;
 *   3. calls onChange(cut). Analytics belong in the site's onChange; nothing
 *      here reports anywhere, and nothing persists beyond the cookie.
 *
 * Base cut: choosing the base REMOVES data-cut from <html> rather than
 * setting it to the base name. The engine renders the base cut when the
 * attribute is absent, when it names the base and when it names a cut the
 * identity does not declare (dcyfr-theme-engine.css, "The cut axis"), and
 * the flagship stamps nothing for a missing or unknown cookie, so absence is
 * the canonical form and the served and the live DOM agree. The cookie still
 * records the base name, so an explicit choice of the base survives a later
 * change of the site's default. A site reading the cookie should treat the
 * base name like a missing value: stamp nothing.
 *
 * The chooser never touches data-identity, never stamps data-identity or
 * data-cut on any element of its own (audit:sites treats a stamp below
 * <html> as a defect), and never reads --sheen: each option's swatch is a
 * static fill from the `swatches` prop, which the site fills from its
 * vendored theme file (each cut's --accent-600 is the intended value). A
 * live var() would paint every swatch in the current cut's colour.
 *
 * Every option shows its name in words beside the swatch (design.md: a cut
 * is named in words, never by swatch alone; the swatch is a hint that
 * survives neither greyscale nor forced colours on its own). `compact`
 * hides the names visually for a crowded header; they stay in the
 * accessibility tree and as titles. With obsidian's five named options the control is
 * wider than a phone header allows, and the options wrap rather than
 * overflow.
 *
 * Mount point: beside the theme toggle, through dcyfr-site-header's
 * `actions` slot. That slot renders at both breakpoints (the desktop
 * actions row and the mobile header row), so pass className="max-md:hidden"
 * to keep the phone header to its toggle and hamburger, or mount a compact
 * instance for it. dcyfr-mobile-nav renders its own theme toggle in the
 * drawer footer with no slot, so the drawer carries no chooser until that
 * item grows one. Satellites do not mount this at all: a satellite pins one
 * cut at build time (dcyfr-theme-engine spec, "The cut axis").
 *
 * Accessibility: a labelled radio group (role="radiogroup" named by the
 * caption, "<Identity> cut" by default), one role="radio" button per cut
 * with aria-checked, roving tabindex, arrow keys (Left/Up and Right/Down
 * wrap, Home/End jump; Space and Enter are the button's own click), a
 * focus ring on keyboard focus only (focus-visible), 44px targets via
 * touch-target (dcyfr-chrome-utilities), and a polite live region that
 * announces the cut after a change. The checked option underlines its
 * name, so the selection reads without colour; the swatch keeps its colour
 * under forced colours (forced-color-adjust: none) and the transparent
 * outline behind the ring becomes the system focus outline there.
 *
 * The shadcn CLI strips this leading comment on install; the notes inside
 * the component are the copy that survives.
 */

import { useId, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { cn } from "@/lib/utils";

/** The cookie the flagship reads to stamp data-cut server-side. */
export const DEFAULT_CUT_COOKIE = "dcyfr-cut";

const CUT_ATTRIBUTE = "data-cut";
const SWAP_CLASS = "theme-swap";
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

export interface CutChooserProps {
  /** The identity whose cuts these are. Names the group; never stamped. */
  identity: string;
  /** The identity's base cut. Choosing it removes data-cut from <html>. */
  base: string;
  /**
   * Every cut the identity declares, base included, in display order, as
   * the theme file names them (lower-case slugs).
   */
  cuts: readonly string[];
  /**
   * One static swatch colour per cut, any CSS colour string. The site reads
   * these from its vendored theme file; the cut's --accent-600 is the
   * intended value.
   */
  swatches: Readonly<Record<string, string>>;
  /** Controlled value. Leave undefined to let the chooser hold its own. */
  value?: string;
  /**
   * Initial value when uncontrolled; the server reads it from the cookie.
   * Defaults to `base`. A value the identity does not declare renders as
   * the base cut, as the engine would.
   */
  defaultValue?: string;
  /** Called after the cookie and the stamp are written. Wire analytics here. */
  onChange?: (cut: string) => void;
  /** Cookie the server reads to stamp data-cut. */
  cookieName?: string;
  /**
   * Display names per cut. A missing entry falls back to the slug in words:
   * "natural-blue" reads "Natural blue".
   */
  labels?: Readonly<Partial<Record<string, string>>>;
  /** Group caption. Defaults to "<Identity> cut". */
  label?: string;
  /**
   * Hide the option names visually and show the swatches alone. The names
   * stay in the accessibility tree and as titles. Off by default: a cut is
   * named in words, and a swatch alone does not survive greyscale.
   */
  compact?: boolean;
  className?: string;
}

/** "natural-blue" -> "Natural blue". */
export function cutLabel(cut: string): string {
  const words = cut.replace(/[-_]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function writeCutCookie(name: string, cut: string) {
  // Secure only over https: a Secure cookie set on http is silently dropped,
  // which would make a local dev server look like a broken chooser.
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(cut)}; Path=/; Max-Age=${ONE_YEAR_SECONDS}; SameSite=Lax${secure}`;
}

function stampCut(cut: string | null) {
  // Same one-frame mechanism as the registry gallery: the engine's
  // .theme-swap suppresses transitions while the stamp changes, and two
  // frames guarantee the new values have painted before it lifts.
  const root = document.documentElement;
  root.classList.add(SWAP_CLASS);
  // The base cut is the absence of the attribute, never its name; see the
  // engine's "The cut axis" note.
  if (cut === null) root.removeAttribute(CUT_ATTRIBUTE);
  else root.setAttribute(CUT_ATTRIBUTE, cut);
  requestAnimationFrame(() => {
    requestAnimationFrame(() => root.classList.remove(SWAP_CLASS));
  });
}

type SwatchStyle = CSSProperties & { "--swatch": string };

export function CutChooser({
  identity,
  base,
  cuts,
  swatches,
  value,
  defaultValue,
  onChange,
  cookieName = DEFAULT_CUT_COOKIE,
  labels,
  label,
  compact = false,
  className,
}: CutChooserProps) {
  const [internal, setInternal] = useState(defaultValue ?? base);
  const [announced, setAnnounced] = useState<string | null>(null);
  const captionId = useId();
  const options = useRef(new Map<string, HTMLButtonElement>());

  const selected = value ?? internal;
  // An undeclared value renders the base cut, which is what the engine does
  // with an undeclared data-cut.
  const current = cuts.includes(selected) ? selected : base;
  const caption = label ?? `${cutLabel(identity)} cut`;
  const nameOf = (cut: string) => labels?.[cut] ?? cutLabel(cut);

  const choose = (cut: string, moveFocus: boolean) => {
    if (moveFocus) options.current.get(cut)?.focus();
    if (cut === current) return;
    if (value === undefined) setInternal(cut);
    writeCutCookie(cookieName, cut);
    stampCut(cut === base ? null : cut);
    setAnnounced(nameOf(cut));
    onChange?.(cut);
  };

  // WAI-ARIA radio group: arrows move the check with the focus and wrap;
  // Home and End jump. Enter and Space are the button's own click.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = Math.max(cuts.indexOf(current), 0);
    let next: number;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = (index + 1) % cuts.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = (index - 1 + cuts.length) % cuts.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = cuts.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    choose(cuts[next], true);
  };

  return (
    <div data-slot="cut-chooser" className={cn("inline-flex items-center", className)}>
      <span id={captionId} className="sr-only">
        {caption}
      </span>
      {/* The options wrap rather than overflow: five named options are wider
          than a phone header, and a clipped option is an unreachable cut. */}
      <div
        role="radiogroup"
        aria-labelledby={captionId}
        className="flex flex-wrap items-center gap-x-1"
        onKeyDown={onKeyDown}
      >
        {cuts.map((cut) => {
          const checked = cut === current;
          const style: SwatchStyle = { "--swatch": swatches[cut] ?? "transparent" };
          return (
            <button
              key={cut}
              ref={(node) => {
                if (node) options.current.set(cut, node);
                else options.current.delete(cut);
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={checked ? 0 : -1}
              title={nameOf(cut)}
              data-value={cut}
              // focus-visible:outline-hidden keeps a transparent outline
              // under the ring, which forced-colours mode paints as the
              // system focus outline; outline-none would leave nothing.
              className={cn(
                "group touch-target gap-2 rounded-md hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                compact ? "px-0" : "px-2",
              )}
              onClick={() => choose(cut, false)}
            >
              <span
                aria-hidden="true"
                style={style}
                // forced-color-adjust-none keeps the swatch's colour under
                // forced colours; the name beside it carries the meaning.
                className="block size-4 shrink-0 rounded-full bg-[var(--swatch)] forced-color-adjust-none ring-1 ring-border group-aria-checked:ring-2 group-aria-checked:ring-foreground group-aria-checked:ring-offset-2 group-aria-checked:ring-offset-background"
              />
              <span
                className={cn(
                  "text-sm",
                  compact ? "sr-only" : "decoration-2 underline-offset-4 group-aria-checked:underline",
                )}
              >
                {nameOf(cut)}
              </span>
            </button>
          );
        })}
      </div>
      <span aria-live="polite" className="sr-only">
        {announced ? `${caption}: ${announced}` : ""}
      </span>
    </div>
  );
}
